import React from 'react';
import { FormattedNumber, useIntl } from 'react-intl';

import { CheckoutPortalFormattedMessage } from 'components/Core/CheckoutPrtalForamattedMessage/CheckoutPortalFormattedMessage';
import { getIntervalSentence } from 'components/Subscription/utils';
import { getLegalLinks } from 'shared/clientUtils';
import { formatDateMMDDYYWithoutZeros, getMonthIndexFromName } from 'shared/dates';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { saleSelectors, forcedAutopayStartDateSelector } from 'store/sale/selectors';
import { RootState } from 'store/store';
import { useSelector } from 'store/utils';
import { colors, fontSize } from 'styles/cp';

const ForcedAutopayConsent: React.FC = () => {
  const companyName = useSelector((state: RootState) =>
    companyInfoSelectors.nameSelector(state.companyInfo)
  );
  const currency = useSelector((state: RootState) => saleSelectors.currencySelector(state.sale));
  const amount = useSelector((state: RootState) => saleSelectors.amountSelector(state.sale));
  const taxAmount = useSelector((state: RootState) => saleSelectors.taxAmountSelector(state.sale));
  const recurringInfo = useSelector((state: RootState) =>
    saleSelectors.getRecurringInfo(state.sale)
  );
  const autopayStartDate = useSelector(forcedAutopayStartDateSelector);

  const intl = useIntl();

  const intervalText = getIntervalSentence({
    frequency: recurringInfo?.intervalType,
    frequencyInterval: recurringInfo?.numberOfInterval || 1,
    monthIndex: recurringInfo?.monthOfYear
      ? getMonthIndexFromName(recurringInfo.monthOfYear)
      : undefined,
    weekIndex: recurringInfo?.weekOfMonth,
    daysOfWeek: recurringInfo?.dayOfWeek ? [recurringInfo.dayOfWeek] : undefined,
    daysOfMonth: recurringInfo?.dayOfMonth ? [recurringInfo.dayOfMonth] : undefined,
    startDate: recurringInfo?.startDate?.toString(),
  }).toLowerCase();

  const startDate = autopayStartDate.getTime().toString();
  const style = {
    fontSize: fontSize.xxs,
    color: colors.darkGray,
  };
  const { privacyUrl, tosUrl } = getLegalLinks(intl.locale);

  const pretaxAmount = taxAmount ? amount - taxAmount : amount;
  const formattedAmount = (
    <FormattedNumber style="currency" currency={currency} value={pretaxAmount} />
  );

  const consentDefaultMessage = `<b>By selecting Pay and set up autopay, I authorize {companyName} to charge the payment method listed above on {startDate} and {interval} thereafter at {amount}, plus tax, unless I cancel. I can cancel anytime by clicking on the manage autopay link in the emails.</b> I agree to the <tosLink>Terms of Service</tosLink> and that I've read and acknowledged Intuit's <privacyLink>Global Privacy Statement</privacyLink>. I also provide written instructions for Intuit Inc. to obtain and periodically refresh information from bank verification data providers to validate my bank account information and for other fraud detection purposes.`;

  return (
    <div className="forced-autopay-consent-cta">
      <div style={style} id="cta-description">
        <CheckoutPortalFormattedMessage
          id="FORCED_AUTOPAY_CONSENT"
          defaultMessage={consentDefaultMessage}
          values={{
            companyName,
            startDate: formatDateMMDDYYWithoutZeros(startDate),
            interval: intervalText,
            amount: formattedAmount,
            b: (chunks: string) => <span className="bold-text">{chunks}</span>,
            tosLink: (chunks: string) => (
              <a className="link" href={tosUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            privacyLink: (chunks: string) => (
              <a className="link" href={privacyUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
          }}
        />
      </div>

      <style jsx>{`
        .forced-autopay-consent-cta {
          padding: 16px 0;
        }

        .bold-text {
          font-family: AvenirNextforINTUIT-Demi;
        }

        .link {
          color: ${colors.blue};
          text-decoration: none;
        }

        .link:hover {
          color: ${colors.blue05};
        }
      `}</style>
    </div>
  );
};

export default ForcedAutopayConsent;
